import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { CheckCircle2 } from 'lucide-react'; // Modern list icon
import { useNavigate } from 'react-router-dom';

const PricingPlans = () => {
  // Original state and logic preserved
  const [isYearly, setIsYearly] = useState(false);
  const navigate = useNavigate();

  // Original plans data preserved
  const plans = [
    {
      name: 'Free',
      monthlyPrice: 0,
      color: '#a5b4fc', 
      popular: false,
      description: 'Great for getting started',
      features: [
        'Unlimited Invoices',
        'Add Customers',
        'Add Products',
        'Bank Transactions',
      ],
    },
    {
      name: 'Basic',
      monthlyPrice: 1000,
      color: '#c4b5fd', 
      popular: true,
      description: 'Best for small teams',
      features: [
        'Unlimited Invoices & Expenses',
        'Inventory Management',
        'Investment Tracking',
        'P&L Reports',
        '24/7 Support',
      ],
    },
    {
      name: 'Premium',
      monthlyPrice: 2500,
      color: '#99f6e4', 
      popular: false,
      description: 'For growing enterprises',
      features: [
        'All Basic Features',
        'Returns Management',
        'Outstanding & Summary Reports',
        'Customer/Product Analytics',
        'Premium Support',
      ],
    },
  ];

  // Original routing function preserved
  const handleSubscribe = () => {
    navigate('/login');
  };

  return (
    <Box 
      sx={{ 
        py: { xs: 8, md: 12 }, 
        backgroundColor: '#F8FAFC', 
        backgroundImage: 'radial-gradient(at 100% 0%, rgba(79, 70, 229, 0.08) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(16, 185, 129, 0.05) 0px, transparent 50%)',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          align="center"
          sx={{ 
            color: '#0F172A', 
            fontWeight: 800, 
            fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
            letterSpacing: '-0.03em',
            mb: 2 
          }}
        >
          Choose Your Plan
        </Typography>
        <Typography 
          variant="body1" 
          textAlign="center" 
          sx={{ 
            color: '#475569', 
            mb: 5,
            fontSize: '1.125rem',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Simple pricing. No hidden fees. Choose monthly or yearly billing.
        </Typography>

        {/* Modern Segmented Toggle Button */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Box
            sx={{
              display: 'inline-flex',
              bgcolor: '#F1F5F9', // Soft gray background
              borderRadius: '999px',
              p: 0.5,
              border: '1px solid #E2E8F0'
            }}
          >
            <Button
              onClick={() => setIsYearly(false)}
              disableElevation
              sx={{
                textTransform: 'none',
                px: 4,
                py: 1,
                fontWeight: 600,
                fontSize: '0.95rem',
                bgcolor: !isYearly ? '#ffffff' : 'transparent',
                color: !isYearly ? '#0F172A' : '#64748B',
                borderRadius: '999px',
                boxShadow: !isYearly ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                '&:hover': {
                  bgcolor: !isYearly ? '#ffffff' : 'rgba(0,0,0,0.02)',
                },
              }}
            >
              Monthly
            </Button>
            <Button
              onClick={() => setIsYearly(true)}
              disableElevation
              sx={{
                textTransform: 'none',
                px: 4,
                py: 1,
                fontWeight: 600,
                fontSize: '0.95rem',
                bgcolor: isYearly ? '#ffffff' : 'transparent',
                color: isYearly ? '#0F172A' : '#64748B',
                borderRadius: '999px',
                boxShadow: isYearly ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                '&:hover': {
                  bgcolor: isYearly ? '#ffffff' : 'rgba(0,0,0,0.02)',
                },
              }}
            >
              Yearly
            </Button>
          </Box>
        </Box>

        {/* Discount Chip */}
        <Box 
          display="flex" 
          justifyContent="center" 
          mb={6} 
          sx={{ height: '32px' /* Fixed height prevents layout shift when toggling */ }}
        >
          {isYearly && (
            <Chip
              label="Save 10% with yearly billing!"
              sx={{
                background: '#ECFDF5',
                color: '#10B981',
                fontWeight: 700,
                px: 1,
                fontSize: '0.85rem',
                border: '1px solid #A7F3D0'
              }}
            />
          )}
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
          {plans.map((plan, idx) => {
            // Original calculations preserved exactly
            const monthly = plan.monthlyPrice;
            const yearly = monthly * 12 * 0.9;
            const originalYear = monthly * 12;
            const displayPrice = isYearly ? yearly : monthly;

            const formattedPrice =
              displayPrice === 0
                ? 'Free'
                : `LKR ${displayPrice.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}`;

            return (
              <Grid item xs={12} md={4} key={idx} sx={{ display: 'flex' }}>
                <Card
                  elevation={0}
                  sx={{
                    position: 'relative',
                    overflow: 'visible',
                    borderRadius: '24px',
                    border: plan.popular ? '2px solid #4F46E5' : '1px solid #E2E8F0',
                    boxShadow: plan.popular 
                      ? '0 25px 50px -12px rgba(79, 70, 229, 0.15)' 
                      : '0 10px 25px -5px rgba(0,0,0,0.05)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-5px)',
                      boxShadow: plan.popular 
                        ? '0 30px 60px -12px rgba(79, 70, 229, 0.25)' 
                        : '0 20px 40px -5px rgba(0,0,0,0.1)',
                    },
                    bgcolor: '#ffffff',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    mt: plan.popular ? { xs: 2, md: -2 } : 0, // Makes popular card slightly taller on desktop
                    mb: plan.popular ? { xs: 0, md: -2 } : 0,
                  }}
                >
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: 4 }}>
                    
                    {/* Floating Popular Badge */}
                    {plan.popular && (
                      <Chip
                        label="Most Popular"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          bgcolor: '#4F46E5',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          boxShadow: '0 4px 14px rgba(79, 70, 229, 0.4)',
                        }}
                      />
                    )}

                    <Typography variant="h5" sx={{ color: '#0F172A', fontWeight: 800, mb: 1 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
                      {plan.description}
                    </Typography>

                    {/* Price Display */}
                    <Box sx={{ mb: 1, minHeight: '70px' }}>
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800, 
                          color: '#0F172A', 
                          fontSize: displayPrice === 0 ? '3rem' : '1.75rem',
                          lineHeight: 1.2
                        }}
                      >
                        {formattedPrice}
                        {displayPrice > 0 && (
                           <Typography component="span" sx={{ fontSize: '1rem', color: '#64748B', fontWeight: 500 }}>
                             {isYearly ? ' / year' : ' / month'}
                           </Typography>
                        )}
                      </Typography>

                      {/* Original Strikethrough Logic */}
                      {isYearly && monthly > 0 && (
                        <Typography
                          variant="body2"
                          sx={{ textDecoration: 'line-through', color: '#94A3B8', mt: 1 }}
                        >
                          LKR {originalYear.toLocaleString(undefined, { minimumFractionDigits: 2 })} / year
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ my: 3, borderColor: '#F1F5F9' }} />

                    {/* Features List with Modern Icons */}
                    <Stack spacing={2} sx={{ mb: 4, flexGrow: 1 }}>
                      {plan.features.map((feature, i) => (
                        <Stack direction="row" spacing={1.5} key={i} alignItems="flex-start">
                          <Box sx={{ color: plan.popular ? '#4F46E5' : '#10B981', mt: 0.2 }}>
                            <CheckCircle2 size={20} />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ color: '#475569', lineHeight: 1.5, fontSize: '0.9rem' }}
                          >
                            {feature}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Box mt="auto">
                      <Button
                        fullWidth
                        size="large"
                        variant={plan.popular ? 'contained' : 'outlined'}
                        onClick={handleSubscribe} // Original routing kept
                        sx={{
                          py: 1.5,
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          backgroundColor: plan.popular ? '#4F46E5' : 'transparent',
                          color: plan.popular ? 'white' : '#0F172A',
                          borderColor: plan.popular ? 'none' : '#E2E8F0',
                          '&:hover': {
                            backgroundColor: plan.popular ? '#4338CA' : '#F8FAFC',
                            borderColor: plan.popular ? 'none' : '#CBD5E1',
                          }
                        }}
                      >
                        {plan.name === 'Free' ? 'Start for Free' : 'Choose Plan'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingPlans;